// src/api/cache.ts
var MemoryRequestCache = class {
  store = /* @__PURE__ */ new Map();
  maxEntries;
  defaultTtlMs;
  constructor(opts = {}) {
    this.maxEntries = opts.maxEntries ?? 200;
    this.defaultTtlMs = opts.defaultTtlMs ?? 3e4;
  }
  get(key) {
    const entry = this.store.get(key);
    if (!entry) return void 0;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return void 0;
    }
    this.store.delete(key);
    this.store.set(key, entry);
    return entry.value;
  }
  set(key, value, ttlMs = this.defaultTtlMs) {
    if (this.store.size >= this.maxEntries) {
      const oldest = this.store.keys().next().value;
      if (oldest !== void 0) this.store.delete(oldest);
    }
    this.store.set(key, { value, expiresAt: Date.now() + ttlMs });
  }
  delete(key) {
    this.store.delete(key);
  }
  clear() {
    this.store.clear();
  }
  keys() {
    return [...this.store.keys()];
  }
};

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var sinks = [];
var minConsoleLevel = "warn";
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  OTHER: "other"
};

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  "0x8f": "monad",
  "0x2611": "plasma",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x145": "zkSync_era_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "zkSync_era_testnet",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "zetachain"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  "0xa4b1": { token: "ARB", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ARB", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma Mainnet" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  monad: "https://monadexplorer.com",
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  mumbai: "https://mumbai.polygonscan.com",
  plasma: "https://plasmascan.to"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  zkSync_era_testnet: "zkSync_era",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei"
};

// src/chains/registry.ts
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(caip2.trim().toLowerCase()) ?? null;
  }
  /** Resolve by CAIP-2, hex, OR slug. */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    return this.get(value.toLowerCase());
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
var registry = new ChainRegistry();
function getChainMetadata(chainId) {
  return registry.resolve(chainId);
}
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug, mainnetSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? (mainnetSlug ? EXPLORER_URLS[mainnetSlug] ?? EXPLORER_SUPPLEMENT[mainnetSlug] ?? null : null);
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4usfv8p8njdtrepy1vzqkqzkvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC" },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL" },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT" },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR" },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC" }
};
function layerFor(slug) {
  if (LAYER1_CHAINS.includes(slug)) return "l1";
  if (LAYER2_CHAINS.includes(slug)) return "l2";
  if (SIDECHAINS.includes(slug)) return "sidechain";
  return "unknown";
}
function seedFromCanonical() {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      registry.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug, mainnetSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (registry.get(slug)) continue;
    try {
      registry.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: false,
          mainnetSlug: null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? 12,
          explorerUrl: explorerFor(slug, slug, null),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {}
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
seedFromCanonical();

// src/value/pricing.ts
var log = createLogger("fe-commons:pricing");
function priceRefKey(ref) {
  const chain = ref.chainId ? getChainMetadata(ref.chainId)?.slug ?? ref.chainId : "*";
  const id = (ref.address ?? ref.symbol ?? "?").toString().toLowerCase();
  return `${chain}:${id}`;
}
var StaticPriceSource = class {
  constructor(prices) {
    this.prices = prices;
  }
  prices;
  name = "static";
  async fetchPrice(ref) {
    const price = this.prices[priceRefKey(ref)] ?? (ref.symbol ? this.prices[`*:${ref.symbol.toLowerCase()}`] : void 0);
    if (price == null) return null;
    return { priceUsd: price, asOf: Date.now(), source: this.name };
  }
};
var PriceEngine = class {
  sources;
  cache;
  freshMs;
  pending = /* @__PURE__ */ new Map();
  /** Never-expiring last-known quotes, used when every source fails. */
  lastKnown = /* @__PURE__ */ new Map();
  constructor(opts) {
    this.sources = opts.sources;
    this.freshMs = opts.freshMs ?? 6e4;
    this.cache = opts.cache ?? new MemoryRequestCache({ defaultTtlMs: this.freshMs, maxEntries: 500 });
  }
  /** Resolve a single price (cached → sources in order → stale fallback → null). */
  getPrice(ref) {
    const key = priceRefKey(ref);
    const fresh = this.cache.get(key);
    if (fresh) return Promise.resolve(fresh);
    const inFlight = this.pending.get(key);
    if (inFlight) return inFlight;
    const p = this.resolveThroughSources(ref, key).finally(() => this.pending.delete(key));
    this.pending.set(key, p);
    return p;
  }
  /** Batch resolve. Returns a map keyed by `priceRefKey`. */
  async getPrices(refs) {
    const out = /* @__PURE__ */ new Map();
    await Promise.all(
      refs.map(async (ref) => {
        out.set(priceRefKey(ref), await this.getPrice(ref));
      })
    );
    return out;
  }
  /** Drop a cached quote (e.g. on manual refresh). */
  invalidate(ref) {
    this.cache.delete(priceRefKey(ref));
  }
  async resolveThroughSources(ref, key) {
    for (const source of this.sources) {
      try {
        const quote = await source.fetchPrice(ref);
        if (quote && Number.isFinite(quote.priceUsd)) {
          const resolved = { ...quote, source: quote.source || source.name };
          this.cache.set(key, resolved, this.freshMs);
          this.lastKnown.set(key, resolved);
          return resolved;
        }
      } catch (err) {
        log.warn(`source ${source.name} failed for ${key}`, { err: String(err) });
      }
    }
    const stale = this.lastKnown.get(key);
    if (stale) {
      log.debug(`serving stale price for ${key} (all sources failed)`);
      return { ...stale, stale: true };
    }
    return null;
  }
};
function formatTokenAmount(amount, token = {}, opts = {}) {
  const decimals = token.decimals ?? 18;
  const { withSymbol = true, withUsd = false, compactUsd = false, maxFractionDigits = 6 } = opts;
  const human = typeof amount === "number" ? (
    // Plain digits first: `String(1e-9)` is `"1e-9"`, which no trim below
    // recognises as a fraction and which reaches the screen as-is.
    trimFraction(toPlainDecimalString(amount), maxFractionDigits)
  ) : formatTokenDisplay(amount, decimals, { maxFractionDigits });
  let out = withSymbol && token.symbol ? `${human} ${token.symbol}` : human;
  if (withUsd && token.priceUsd != null) {
    const humanNum = typeof amount === "number" ? amount : Number(formatUnits(amount, decimals));
    out += ` (${formatUsd(humanNum * token.priceUsd, { compact: compactUsd })})`;
  }
  return out;
}
function trimFraction(value, maxFractionDigits) {
  if (!value.includes(".")) return value;
  const [w, f] = value.split(".");
  const trimmed = f.slice(0, maxFractionDigits).replace(/0+$/, "");
  if (trimmed) return `${w}.${trimmed}`;
  if (!isZeroText(w) || /[1-9]/.test(f ?? "")) {
    return formatTokenDisplay(parseUnits(value, f.length), f.length, {
      maxFractionDigits
    });
  }
  return w;
}
function nativeTokenMeta(chainId) {
  const chain = getChainMetadata(chainId);
  return { symbol: chain?.nativeToken, decimals: chain?.decimals ?? 18 };
}

// src/chains/address.ts
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BASE58 = /^(1|3)[1-9A-HJ-NP-Za-km-z]{25,39}$/;
var RE_BTC_BECH32 = /^(bc1|tb1)[0-9ac-hj-np-z]{11,71}$/;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (chainId) {
    const fam = getChainMetadata(chainId)?.family;
    if (fam) return fam;
  }
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (RE_BTC_BASE58.test(address) || RE_BTC_BECH32.test(address)) return ChainFamily.UTXO;
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function normalizeAddress(address, chainId) {
  const addr = address.trim();
  const fam = addressFamily(addr, chainId);
  if (fam === ChainFamily.EVM) {
    const hex = addr.toLowerCase();
    return hex.startsWith("0x") ? hex : `0x${hex}`;
  }
  return addr;
}
function addressesEqual(a, b, chainId) {
  if (!a || !b) return false;
  return normalizeAddress(a, chainId) === normalizeAddress(b, chainId);
}

// src/value/netflow.ts
function tokenKeyOf(t) {
  return `${t.chainId ?? "*"}:${t.token.toLowerCase()}`;
}
function computeNetFlow(transfers, focalAddress) {
  const map = /* @__PURE__ */ new Map();
  for (const t of transfers) {
    const amount = typeof t.amount === "bigint" ? t.amount : BigInt(t.amount);
    const key = tokenKeyOf(t);
    let entry = map.get(key);
    if (!entry) {
      entry = {
        tokenKey: key,
        token: t.token,
        symbol: t.symbol ?? null,
        chainId: t.chainId ?? null,
        decimals: t.decimals,
        in: 0n,
        out: 0n,
        net: 0n,
        priceUsd: t.priceUsd ?? null
      };
      map.set(key, entry);
    }
    if (entry.priceUsd == null && t.priceUsd != null) entry.priceUsd = t.priceUsd;
    if (addressesEqual(focalAddress, t.to, t.chainId)) entry.in += amount;
    if (addressesEqual(focalAddress, t.from, t.chainId)) entry.out += amount;
  }
  for (const entry of map.values()) {
    entry.net = entry.in - entry.out;
    if (entry.priceUsd != null) entry.netUsd = tokenToUsd(entry.net, entry.decimals, entry.priceUsd);
  }
  return map;
}
function summarizeSwap(transfers, focalAddress) {
  const entries = [...computeNetFlow(transfers, focalAddress).values()].filter((e) => e.net !== 0n);
  return {
    sent: entries.filter((e) => e.net < 0n),
    received: entries.filter((e) => e.net > 0n)
  };
}
function aggregateNetUsd(entries) {
  let total = 0;
  for (const e of entries) if (e.netUsd != null) total += e.netUsd;
  return total;
}
function counterparties(transfers, focalAddress) {
  const set = /* @__PURE__ */ new Set();
  for (const t of transfers) {
    const focalIsFrom = addressesEqual(focalAddress, t.from, t.chainId);
    const focalIsTo = addressesEqual(focalAddress, t.to, t.chainId);
    if (focalIsFrom && !focalIsTo) set.add(normalizeAddress(t.to, t.chainId));
    if (focalIsTo && !focalIsFrom) set.add(normalizeAddress(t.from, t.chainId));
  }
  return [...set];
}

// src/value/index.ts
function parseUnits(value, decimals) {
  const negative = value.trim().startsWith("-");
  const clean = value.trim().replace(/^[-+]/, "");
  const [whole, frac = ""] = clean.split(".");
  const fracPadded = (frac + "0".repeat(decimals)).slice(0, decimals);
  const combined = `${whole || "0"}${fracPadded}`.replace(/^0+(?=\d)/, "");
  const result = BigInt(combined || "0");
  return negative ? -result : result;
}
function toBaseUnits(value) {
  if (typeof value === "bigint") return value;
  const text = value.trim();
  if (!/^[-+]?\d+$/.test(text)) return void 0;
  try {
    return BigInt(text);
  } catch {
    return void 0;
  }
}
function formatUnits(value, decimals) {
  const parsed = toBaseUnits(value);
  const scale = Number.isInteger(decimals) && decimals >= 0 ? decimals : void 0;
  if (parsed === void 0 || scale === void 0) {
    return typeof value === "string" ? value.trim() : String(value);
  }
  let v = parsed;
  const negative = v < 0n;
  if (negative) v = -v;
  const base = 10n ** BigInt(scale);
  const whole = v / base;
  const frac = v % base;
  if (scale === 0) return `${negative ? "-" : ""}${whole.toString()}`;
  const fracStr = frac.toString().padStart(scale, "0").replace(/0+$/, "");
  const body = fracStr ? `${whole.toString()}.${fracStr}` : whole.toString();
  return `${negative ? "-" : ""}${body}`;
}
function tokenToUsd(amount, decimals, priceUsd) {
  const human = Number(formatUnits(amount, decimals));
  if (!Number.isFinite(human) || !Number.isFinite(priceUsd)) return 0;
  return human * priceUsd;
}
function aggregateUsd(holdings) {
  let total = 0;
  for (const h of holdings) {
    if (h.priceUsd == null) continue;
    total += tokenToUsd(h.amount, h.decimals, h.priceUsd);
  }
  return total;
}
function sumAmounts(amounts) {
  let total = 0n;
  for (const a of amounts) total += toBaseUnits(a) ?? 0n;
  return total;
}
var USD_FORMATTER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
var SUB_CENT_USD = 5e-3;
function subCentUsdBound(value) {
  return value < 0 ? ">-$0.01" : "<$0.01";
}
function formatShare(value) {
  if (!Number.isFinite(value) || value === 0) return "0%";
  const pct = value * 100;
  if (Math.abs(pct) < 1) return pct > 0 ? "<1%" : ">-1%";
  return `${pct % 1 === 0 ? pct : pct.toFixed(1)}%`;
}
function formatUsd(value, opts = {}) {
  if (value !== 0 && Number.isFinite(value) && Math.abs(value) < SUB_CENT_USD) {
    return subCentUsdBound(value);
  }
  if (opts.compact || Math.abs(value) >= 1e4) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 2
    }).format(value);
  }
  return USD_FORMATTER.format(value);
}
var COMPACT_BALANCE = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 2
});
function formatTokenBalance(value) {
  if (value == null || !Number.isFinite(value)) return "\u2014";
  const abs = Math.abs(value);
  if (abs === 0) return "0";
  if (abs >= 1e6) return COMPACT_BALANCE.format(value);
  if (abs >= 1) {
    return value.toLocaleString("en-US", { maximumFractionDigits: 4 });
  }
  if (abs < 1e-4) return value < 0 ? ">-0.0001" : "<0.0001";
  return value.toLocaleString("en-US", { maximumFractionDigits: 6 });
}
var SUB_PRECISION_SIGNIFICANT_DIGITS = 2;
var SUB_PRECISION_MAX_FRACTION = 12;
var MAX_PLAIN_DECIMAL_CHARS = 40;
function subPrecisionAmount(whole, fraction, maxFrac) {
  const firstSignificant = fraction.search(/[1-9]/);
  const keep = firstSignificant + SUB_PRECISION_SIGNIFICANT_DIGITS;
  if (firstSignificant >= 0 && keep <= SUB_PRECISION_MAX_FRACTION) {
    const kept = fraction.slice(0, keep).replace(/0+$/, "");
    return `${whole}.${kept}`;
  }
  if (!isZeroText(whole)) return whole;
  const bound = `0.${"0".repeat(Math.max(maxFrac - 1, 0))}1`;
  return whole.startsWith("-") ? `>-${bound}` : `<${bound}`;
}
function formatTokenDisplay(amount, decimals, opts = {}) {
  const full = formatUnits(amount, decimals);
  const maxFrac = opts.maxFractionDigits ?? 6;
  let out = full;
  if (full.includes(".")) {
    const w = full.split(".")[0] ?? "0";
    const f = full.split(".")[1] ?? "";
    const kept = f.slice(0, maxFrac).replace(/0+$/, "");
    out = kept ? `${w}.${kept}` : subPrecisionAmount(w, f, maxFrac);
  }
  return opts.symbol ? `${out} ${opts.symbol}` : out;
}
function isZeroText(value) {
  return value === "0" || value === "-0";
}
function toPlainDecimalString(value) {
  if (!Number.isFinite(value)) return String(value);
  const text = String(value);
  const match = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(text);
  if (!match) return text;
  const [, sign, whole, fraction = "", exponentText] = match;
  const digits = `${whole}${fraction}`;
  const exponent = Number(exponentText);
  const point = whole.length + exponent;
  const expanded = point <= 0 ? digits.length - point + 2 : Math.max(point, digits.length) + 1;
  if (expanded > MAX_PLAIN_DECIMAL_CHARS) return text;
  if (point <= 0) return `${sign}0.${"0".repeat(-point)}${digits}`;
  if (point >= digits.length) return `${sign}${digits}${"0".repeat(point - digits.length)}`;
  return `${sign}${digits.slice(0, point)}.${digits.slice(point)}`;
}
function formatGraphAmount(amount, opts = {}) {
  if (amount == null) return null;
  const exact = String(amount).trim();
  if (!exact) return null;
  const maxChars = opts.maxChars ?? 12;
  if (exact.length <= maxChars) {
    return { text: exact, title: exact, abbreviated: false };
  }
  const numeric = DECIMAL_RE.test(exact) ? Number(exact) : Number.NaN;
  if (Number.isFinite(numeric)) {
    if (Math.abs(numeric) >= 1e6) {
      return { text: formatTokenBalance(numeric), title: exact, abbreviated: true };
    }
    const [whole = "", fraction = ""] = exact.split(".");
    const room = Math.max(2, maxChars - whole.length - 2);
    if (fraction.length > room) {
      return { text: `${whole}.${fraction.slice(0, room)}\u2026`, title: exact, abbreviated: true };
    }
  }
  return { text: `${exact.slice(0, maxChars)}\u2026`, title: exact, abbreviated: true };
}
var DECIMAL_RE = /^-?\d+(?:\.\d+)?$/;
var LONG_NUMBER_RE = /\d[\d,]*(?:\.\d+)?/g;
function shortenLongNumbers(text, opts = {}) {
  const maxDigits = opts.maxDigits ?? 12;
  return text.replace(LONG_NUMBER_RE, (match) => {
    if (match.length <= maxDigits) return match;
    const display = formatGraphAmount(match.replace(/,/g, ""), { maxChars: maxDigits });
    return display ? display.text : match;
  });
}
var DECIMAL_TEXT = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
function coerceNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : void 0;
  if (typeof value !== "string") return void 0;
  const text = value.trim();
  if (text === "" || !DECIMAL_TEXT.test(text)) return void 0;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : void 0;
}

export { PriceEngine, SUB_CENT_USD, StaticPriceSource, aggregateNetUsd, aggregateUsd, coerceNumber, computeNetFlow, counterparties, formatGraphAmount, formatShare, formatTokenAmount, formatTokenBalance, formatTokenDisplay, formatUnits, formatUsd, isZeroText, nativeTokenMeta, parseUnits, priceRefKey, shortenLongNumbers, subCentUsdBound, sumAmounts, summarizeSwap, toPlainDecimalString, tokenToUsd };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map